import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Extension } from '@arcnet/contracts';
import { useSession, useHasWorkspace } from '../../lib/auth/session.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { useBrowserPhone, type BrowserPhoneApi } from './useBrowserPhone.js';
import { useShift, type ShiftApi } from './useShift.js';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';

/**
 * The agent's phone and shift, for the whole portal (3 October 2026).
 *
 * They lived in the workspace page; once the workspace was in the console's frame, opening Call
 * history from the menu unmounted it -- signing the phone out, hanging up the call, and letting
 * the shift go quiet. Here they last as long as the person is signed in, the workspace is one view
 * of them, a bar in the header shows a call from any page, and any number can be dialled.
 *
 * The extension is the person's OWN (owner, 3 October 2026: "just use the agents primary
 * extension"): the one linked to them that takes calls in the browser. Only somebody without one
 * -- an admin trying the workspace -- chooses.
 */
/** Notes and tags made during the call, waiting for its record (owner, 3 October 2026). */
export interface InCallWork {
  notes: string[];
  tags: string[];
  addNote: (text: string) => void;
  toggleTag: (tag: string) => void;
  /** The account's tags, to pick from. */
  options: string[];
  /** Set once what was noted has been attached to the call's record. */
  saved: 'idle' | 'waiting' | 'saved' | 'failed';
}

interface PhoneContext {
  seat: Extension | null;
  /** Extensions this person could take calls on, when they have more than one to choose from. */
  choices: Extension[];
  loading: boolean;
  choose: (id: string | null) => void;
  phone: BrowserPhoneApi | null;
  shift: ShiftApi | null;
  /** Bumped when a call ends, so lists of calls refresh. */
  endedAt: number;
  inCall: InCallWork | null;
}

const Ctx = createContext<PhoneContext>({
  seat: null, choices: [], loading: false, choose: () => undefined, phone: null, shift: null, endedAt: 0, inCall: null,
});

export const usePhone = (): PhoneContext => useContext(Ctx);

const read = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };

export function PhoneProvider({ children }: { children: ReactNode }) {
  const tenantId = useSession((s) => s.activeTenantId);
  const userId = useSession((s) => s.session?.user.id ?? null);
  const has = useHasWorkspace();
  const seats = useExtensions(has ? tenantId : null, { limit: 50 });
  const usable = useMemo(() => (seats.data?.data ?? []).filter((e) => e.may_workspace !== false), [seats.data]);
  const own = usable.find((e) => e.user_id === userId) ?? null;
  const key = `arc.ws.seat.${tenantId ?? ''}`;
  const [chosen, setChosen] = useState<string | null>(() => read(key));
  const choose = useCallback((id: string | null) => {
    setChosen(id);
    try { if (id) localStorage.setItem(key, id); else localStorage.removeItem(key); } catch { /* private window */ }
  }, [key]);
  const seat = own ?? usable.find((e) => e.id === chosen) ?? (usable.length === 1 ? usable[0]! : null);

  const base = { seat, choices: own ? [] : usable, loading: has && seats.data === undefined, choose };
  if (!has || !tenantId || !seat) {
    return <Ctx.Provider value={{ ...base, phone: null, shift: null, endedAt: 0, inCall: null }}>{children}</Ctx.Provider>;
  }
  return <SeatPhone key={`${tenantId}:${seat.id}`} tenantId={tenantId} base={base}>{children}</SeatPhone>;
}

function SeatPhone({ tenantId, base, children }: {
  tenantId: string; base: Omit<PhoneContext, 'phone' | 'shift' | 'endedAt' | 'inCall'>; children: ReactNode;
}) {
  const seat = base.seat!;
  const [endedAt, setEndedAt] = useState(0);
  const phone = useBrowserPhone({ tenantId, extensionId: seat.id, onEnded: () => setEndedAt(Date.now()) });
  const shift = useShift(tenantId, seat.id, phone.call !== null, phone.call?.answeredAt != null);
  /**
   * Closing or reloading the tab drops the phone's registration, and a call with it (owner,
   * 6 October 2026, via the ArcSIP session): the browser asks first while the phone is signed in,
   * and always on a call. The shift is the server's and outlives the tab; on a reload the phone
   * signs in again by itself (AgentWorkspace, "Logged in but the phone is not up").
   */
  const holding = phone.call !== null || phone.state === 'ready' || phone.state === 'reconnecting';
  useEffect(() => {
    if (!holding) return;
    const ask = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // Older browsers ask only when this is set; none show the text any more.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [holding]);
  /**
   * Auto-answer (0153): a ringing call is answered by itself after a short tone, wherever in the
   * portal the agent is. Only while logged on: a phone on but off shift rings as usual.
   */
  const ringing = phone.call?.inbound && phone.call.state === 'ringing';
  useEffect(() => {
    if (!ringing || !shift.controls.auto_answer || !shift.shiftId) return;
    try {
      const ctx = new AudioContext();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = 880; g.gain.value = 0.08; o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 0.25);
      setTimeout(() => void ctx.close(), 600);
    } catch { /* no audio: answering still goes ahead */ }
    const t = setTimeout(() => phone.answer(), 1_200);
    return () => clearTimeout(t);
  }, [ringing, shift.controls.auto_answer, shift.shiftId]); // eslint-disable-line react-hooks/exhaustive-deps
  /**
   * Notes and tags during the call. The call's record (its CDR) reaches the platform about fifteen
   * seconds after it ends, and that is what notes hang off; so they are held here and, when the
   * call ends, attached to the extension's last call as soon as it appears -- the same call the
   * wrap-up asks about.
   */
  const [notes, setNotes] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [saved, setSaved] = useState<InCallWork['saved']>('idle');
  const callStart = useRef<number | null>(null);
  const pending = useRef<{ notes: string[]; tags: string[] }>({ notes: [], tags: [] });
  pending.current = { notes, tags };
  const options = useQuery({
    queryKey: ['call-tag-options', tenantId],
    queryFn: () => api.get<{ data: Array<{ label: string; is_active: boolean }> }>(`/v1/tenants/${tenantId}/call-tag-options`),
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    if (phone.call && callStart.current === null) {
      callStart.current = Date.now();
      setNotes([]); setTags([]); setSaved('idle');
    }
  }, [phone.call]);
  useEffect(() => {
    if (!phone.lastEnd || callStart.current === null) return;
    const started = callStart.current;
    callStart.current = null;
    const { notes: n, tags: t } = pending.current;
    if (!n.length && !t.length) return;
    setSaved('waiting');
    let tries = 0;
    let stop = false;
    const attach = async (): Promise<void> => {
      if (stop) return;
      tries++;
      const last = await api.get<{ id: string; started_at: string } | null>(
        `/v1/tenants/${tenantId}/extensions/${base.seat!.id}/last-call`).catch(() => null);
      if (last && Date.parse(last.started_at) >= started - 120_000) {
        try {
          for (const body of n) await api.post(`/v1/tenants/${tenantId}/cdrs/${last.id}/notes`, { body });
          if (t.length) {
            const work = await api.get<{ tags: string[] }>(`/v1/tenants/${tenantId}/cdrs/${last.id}/work`);
            await api.put(`/v1/tenants/${tenantId}/cdrs/${last.id}/tags`, { tags: [...new Set([...work.tags, ...t])] });
          }
          setSaved('saved');
          setNotes([]); setTags([]);
        } catch { setSaved('failed'); }
        return;
      }
      if (tries < 30) setTimeout(() => void attach(), 3_000); else setSaved('failed');
    };
    void attach();
    return () => { stop = true; };
  }, [phone.lastEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const inCall = useMemo<InCallWork>(() => ({
    notes, tags, saved,
    options: (options.data?.data ?? []).filter((o) => o.is_active).map((o) => o.label).slice(0, 8),
    addNote: (text: string) => { const v = text.trim(); if (v) setNotes((x) => [...x, v]); },
    toggleTag: (tag: string) => setTags((x) => (x.includes(tag) ? x.filter((y) => y !== tag) : [...x, tag])),
  }), [notes, tags, saved, options.data]);

  const value = useMemo(() => ({ ...base, phone, shift, endedAt, inCall }), [base, phone, shift, endedAt, inCall]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
